import { Delete } from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { IHeaderParams, RowSelectedEvent } from "ag-grid-community";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomLoader from "../../../../../../util/component/CustomLoader";
import { setSnackbar } from "../../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../../util/state/globalVariables";
import { deleteRulesByIds } from "../../../../api/services";
import DialogCloseButton from "../../../../components/DialogCloseButton";
import { BookingScope, IRule } from "../../../../types";
import { hasScope } from "../../../../utils";

const HeaderActions = (params: IHeaderParams<IRule>) => {

    const { t } = useTranslation('booking');

    const { dispatchGlobalData, globalData } = useContext(GlobalContext);

    const deleteRulesByIdsMutation = useMutation(
        deleteRulesByIds,
        {
            onSettled: () => {
                setDialogOpen(false)
            },
            onSuccess: () => {
                params.api.refreshServerSide();
            },
            onError: () => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        }
    );

    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [selectedRows, setSelectedRows] = useState<IRule[]>([]);

    const handleShowDialog = useCallback(() => {
        setDialogOpen(true);
    }, []);
    const handleCloseDialog = useCallback(() => {
        setDialogOpen(false);
    }, []);
    const handleDelete = useCallback(() => {
        const selectedRowIds = params.api
            .getSelectedRows()
            .map(r => r.id);
        deleteRulesByIdsMutation.mutate(selectedRowIds);
    }, []);
    const onRowSelected = useCallback((e: RowSelectedEvent) => {
        setSelectedRows(e.api.getSelectedRows());
    }, []);

    useEffect(() => {
        params.api.addEventListener('rowSelected', onRowSelected);
        return () => params.api.removeEventListener('rowSelected', onRowSelected);
    }, [])

    return (
        <>
            {hasScope(globalData, BookingScope.DeleteRule) &&
                <Tooltip
                    title={t('label.deleteSelected')}
                    disableInteractive
                    enterDelay={500}
                    enterNextDelay={500}
                >
                    <span>
                        <IconButton
                            onClick={handleShowDialog}
                            disabled={selectedRows.length === 0}
                        >
                            <Delete />
                        </IconButton>
                    </span>
                </Tooltip>
            }
            <Dialog open={dialogOpen} onClose={handleCloseDialog}>
                <DialogCloseButton onClick={handleCloseDialog} />
                <DialogTitle>
                    {t('label.deleteSelected')}
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('message.info.delete_selected_rules')}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handleCloseDialog}>
                        {t('label.cancel')}
                    </Button>
                    <Button onClick={handleDelete}>
                        {t('label.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
            <CustomLoader loading={deleteRulesByIdsMutation.isLoading} />
        </>
    )
};

export default HeaderActions;